import { Box, Button, Typography } from "@mui/material";
import { ReconciliationStatus } from "generated-graphql/graphql";
import { useMemo } from "react";

interface ReconciliationSectionProps {
  paymentId?: string;
  existingPayment?: any;
  notEditable: boolean;
  onShowReconcileDialog: () => void;
}

export function ReconciliationSection({
  paymentId,
  existingPayment,
  notEditable,
  onShowReconcileDialog,
}: ReconciliationSectionProps) {
  // Memoize status text to prevent unnecessary recalculations
  const statusText = useMemo(() => {
    if (!existingPayment?.reconciliationStatus) return "Unreconciled";

    switch (existingPayment.reconciliationStatus) {
      case ReconciliationStatus.Reconciled:
        return "Reconciled";
      case ReconciliationStatus.Problem:
        return "Problem";
      default:
        return "Unreconciled";
    }
  }, [existingPayment?.reconciliationStatus]);

  // Use static button text
  const buttonText = "Update Status";

  // Memoize date formatting to prevent repeated Date object creation
  const dateInfo = useMemo(() => {
    if (!existingPayment?.reconciledAt) return null;

    const date = new Date(existingPayment.reconciledAt);
    const isValid = !isNaN(date.getTime());

    if (!isValid) return null;

    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    const actionText =
      existingPayment?.reconciliationStatus === ReconciliationStatus.Problem
        ? "Marked as problem on "
        : "Reconciled on ";

    return {
      actionText,
      formattedDate,
      reconciledBy: existingPayment?.reconciledBy,
    };
  }, [
    existingPayment?.reconciledAt,
    existingPayment?.reconciliationStatus,
    existingPayment?.reconciledBy,
  ]);

  // Early return for no payment ID
  if (!paymentId) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Reconciliation Status
      </Typography>

      {paymentId && existingPayment ? (
        <Box>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            Status: {statusText}
          </Typography>

          {dateInfo && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {dateInfo.actionText}
              {dateInfo.formattedDate}
              {dateInfo.reconciledBy && (
                <>
                  <br />
                  by {dateInfo.reconciledBy}
                </>
              )}
            </Typography>
          )}

          {!dateInfo && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 2,
                color: "text.secondary",
              }}
            >
              <strong>📋 Standard Operating Procedure</strong>
              <br />
              The finance team will reconcile payments. This is a two-part system of checks and
              balances.
            </Typography>
          )}

          <Button
            variant="outlined"
            size="small"
            disabled={notEditable}
            onClick={onShowReconcileDialog}
          >
            {buttonText}
          </Button>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No payment loaded
        </Typography>
      )}
    </Box>
  );
}
